import React from 'react';

import { Button, HStack, Text, VStack, theme } from '@squire/ui';

import { ChevronRightIcon, ClockIcon } from '@/components/_icons';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { pressedState } from '@/utils/a11y';

import { styles } from './styles';
import type { ChooseTimeEmptyStateProps } from './types';

export function ChooseTimeEmptyState(props: ChooseTimeEmptyStateProps) {
  const {
    onNavigateToNextAvailable,
    showWaitlist = false,
    onWaitlistPress,
    isWaitlistSelected = false,
    waitlistDisabled = false,
    isStandbyWaitlist = false,
    testID,
  } = props;

  const { t } = useScreenTranslations('ChooseTimeEmptyState');
  // Standby follows Figma 16959-31645 (light on brand); legacy keeps its colours.
  const isStandbySelected = isStandbyWaitlist && isWaitlistSelected;

  if (!onNavigateToNextAvailable && !(isStandbyWaitlist && showWaitlist)) {
    return (
      <VStack testID={testID} style={styles.container}>
        <Text variant="sStrong" color="textSecondary">
          {t('noAvailability')}
        </Text>
      </VStack>
    );
  }

  return (
    <HStack space={theme.spacing[3]}>
      {onNavigateToNextAvailable && (
        <Button
          variant="secondary"
          style={styles.container}
          testID={testID}
          onPress={onNavigateToNextAvailable}
          iconLeft={<ChevronRightIcon color="textPrimary" size={16} />}
        >
          <Text variant="sStrong" numberOfLines={1}>
            {t('nextTime')}
          </Text>
        </Button>
      )}

      {showWaitlist && (
        <Button
          variant="secondary"
          style={
            isWaitlistSelected ? styles.containerSelected : styles.container
          }
          testID={`${testID}-waitlist`}
          onPress={onWaitlistPress}
          disabled={waitlistDisabled}
          {...(isStandbyWaitlist ? pressedState(isWaitlistSelected) : {})}
          aria-disabled={(isStandbyWaitlist && waitlistDisabled) || undefined}
          iconLeft={
            <ClockIcon
              color={
                isStandbySelected
                  ? 'iconOnBrand'
                  : isWaitlistSelected
                    ? 'iconOnSelected'
                    : 'textPrimary'
              }
              size={16}
              selected={isWaitlistSelected}
            />
          }
        >
          <Text
            variant="sStrong"
            numberOfLines={1}
            color={
              isStandbySelected
                ? 'textOnBrand'
                : isWaitlistSelected
                  ? 'textOnSelected'
                  : 'textPrimary'
            }
          >
            {t('waitlist')}
          </Text>
        </Button>
      )}
    </HStack>
  );
}
